import {
  LoaderFunctionArgs,
  redirect,
  useNavigate,
  useSearchParams,
 useSubmit } from "react-router"
import { getUserSession } from "~/server/session.server"
import { useEffect, useState } from "react"
import { getAuth } from "firebase/auth"
import { Loader2 } from "lucide-react"
import { firebaseApp } from "~/client/firebase.client"

export async function loader({ request }: LoaderFunctionArgs) {
  // We allow this page to render even without a session, 
  // so the client-side code can attempt to restore the session via Firebase
  return null
}

export default function RefreshToken() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const submit = useSubmit()
  const redirectTo = searchParams.get("redirectTo") || "/"
  // Back to login keeps where the user was headed, and whether they asked for
  // the login page themselves (then it shows the form even for a public page).
  const askedForLogin = searchParams.get("login") === "true"
  const loginUrl = `/login?fromRefresh=true${askedForLogin ? "&login=true" : ""}&redirectTo=${encodeURIComponent(redirectTo)}`
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const auth = getAuth(firebaseApp)
    let timeoutId: NodeJS.Timeout
    let cancelled = false

    // Set a timeout to redirect to login if taking too long
    timeoutId = setTimeout(() => {
      console.error("Token refresh timed out")
      if (!cancelled) navigate(loginUrl)
    }, 5000) // 5 seconds timeout

    const run = async () => {
      try {
        // Wait for Firebase Auth to restore state from persistence before checking.
        // This prevents redirecting to login when auth hasn't loaded yet (e.g. after
        // email verification redirect).
        await auth.authStateReady()
        if (cancelled) return

        const user = auth.currentUser

        // If no user after auth is ready, redirect to login
        if (!user) {
          console.log("No valid auth session found, redirecting to login")
          clearTimeout(timeoutId)
          navigate(loginUrl)
          return
        }

        // Handle email not verified: retry once with reload + force token refresh
        // to account for propagation delay after user just clicked verification link
        let currentUser = user
        if (!currentUser.emailVerified) {
          await currentUser.reload()
          currentUser = auth.currentUser
          if (cancelled || !currentUser) return
          // Force token refresh to get updated email_verified claim
          await currentUser.getIdToken(true)
          if (cancelled) return
          if (!currentUser.emailVerified) {
            console.log("Email not verified, redirecting to login")
            clearTimeout(timeoutId)
            // `unverified` keeps the login loader from applying its
            // public-landing rule: this browser has a Firebase user, it just
            // needs the verification screen.
            navigate(`/login?fromRefresh=true&unverified=true`)
            return
          }
        }

        clearTimeout(timeoutId)

        const idToken = await currentUser.getIdToken(true)
        if (cancelled) return

        const formData = new FormData()
        formData.set("idToken", idToken)
        formData.set("autoLogin", "true")
        submit(formData, {
          method: "post",
          action: "/login?redirectTo=" + encodeURIComponent(redirectTo),
        })
      } catch (err) {
        console.error("Error during token refresh:", err)
        clearTimeout(timeoutId)
        if (!cancelled) {
          setError("Session expired. Please log in again.")
          navigate(loginUrl)
        }
      }
    }

    run()

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [submit, navigate, redirectTo, loginUrl])

  return (
    <div className="h-screen w-screen flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p className="text-muted-foreground">
          {error || "Refreshing session..."}
        </p>
      </div>
    </div>
  )
}
